import clsx from 'clsx';
import { useFormik } from 'formik';
import { isArray } from 'lodash';
import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { convertDollarsToCents } from '../../../../../../utils';
import { useQueryResponse } from '../../../../user-management/users-list/core/QueryResponseProvider';
import { useAmountLimitation } from '../../../core/_requests';

interface Props {
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialValues = {
	amount: undefined,
	verification_code: undefined,
};

const editWithdrawSchema = Yup.object().shape({
	amount: Yup.number().required('Введите сумму'),
	verification_code: Yup.string().required('Введите код'),
});

const AutomaticModalBody: FC<Props> = ({ setIsOpenModal }) => {
	const [error, setError] = useState<any | null>(null);
	const [isSuccess, setIsSuccess] = useState<boolean>(false);
	const { refetch } = useQueryResponse();

	const {
		mutate: setNewAmount,
		isLoading: isSetNewAmountLoading,
		error: setNewAmountError,
	} = useAmountLimitation();

	useEffect(() => {
		if (setNewAmountError) setError(setNewAmountError);
	}, [setNewAmountError]);

	const cancel = () => {
		setError(null);
		setIsOpenModal(false);
	};

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: editWithdrawSchema,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);

			try {
				setNewAmount(
					{
						amount: convertDollarsToCents(values?.amount),
						verification_code: values?.verification_code,
					},
					{
						onSuccess: () => {
							setIsSuccess(true);
							refetch();
							setTimeout(() => {
								cancel();
							}, 2000);
						},
					}
				);
			} catch (ex) {
				console.error(ex);
			} finally {
				setSubmitting(false);
			}
		},
	});

	return (
		<>
			<form
				id='kt_modal_add_user_form'
				className='form p-10'
				onSubmit={formik.handleSubmit}
				noValidate
			>
				{isSuccess && (
					<div className='alert alert-success fw-bold fs-4 text-center m-0'>
						Сумма автовывода успешно изменена
					</div>
				)}
				{/* begin::Scroll */}
				{!isSuccess && (
					<div className='d-flex flex-column me-n7 ps-2 pe-7'>
						{/* begin::Input group */}

						<div className='fv-row mb-7'>
							{/* begin::Label */}
							<label className='required fw-bold fs-6 mb-2'>
								Введите новую сумму
							</label>
							{/* end::Label */}

							{/* begin::Input */}
							<input
								placeholder='0.00$'
								{...formik.getFieldProps('amount')}
								type='tel'
								name='amount'
								className={clsx(
									'form-control form-control-solid mb-3 mb-lg-0',
									{
										'is-invalid': formik.touched.amount && formik.errors.amount,
									},
									{
										'is-valid': formik.touched.amount && !formik.errors.amount,
									}
								)}
								autoComplete='off'
								disabled={formik.isSubmitting}
							/>
							{formik.touched.amount && formik.errors.amount && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.amount}</span>
									</div>
								</div>
							)}
							{/* end::Input */}
						</div>

						<div className='fv-row mb-7'>
							{/* begin::Label */}
							<label className='required fw-bold fs-6 mb-2'>
								Введите код верификации
							</label>
							{/* end::Label */}

							{/* begin::Input */}
							<input
								placeholder='b0r300bda3de466d9215'
								{...formik.getFieldProps('verification_code')}
								type='string'
								name='verification_code'
								className={clsx(
									'form-control form-control-solid mb-3 mb-lg-0',
									{
										'is-invalid':
											formik.touched.verification_code &&
											formik.errors.verification_code,
									},
									{
										'is-valid':
											formik.touched.verification_code &&
											!formik.errors.verification_code,
									}
								)}
								autoComplete='off'
								disabled={formik.isSubmitting}
							/>
							{formik.touched.verification_code &&
								formik.errors.verification_code && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>
												{formik.errors.verification_code}
											</span>
										</div>
									</div>
								)}
							{/* end::Input */}
						</div>
						{/* end::Input group */}
					</div>
				)}

				{/* end::Scroll */}
				{error && (
					<div className='fv-plugins-message-container'>
						{isArray(error?.response?.data?.detail) ? (
							error?.response?.data?.detail?.map((error: any) => (
								<div
									data-field='appname'
									data-validator='notEmpty'
									className='fv-help-block'
								>
									{error.msg}
								</div>
							))
						) : error?.detail ? (
							<div
								data-field='appname'
								data-validator='notEmpty'
								className='fv-help-block'
							>
								{error?.detail}
							</div>
						) : (
							<div
								data-field='appname'
								data-validator='notEmpty'
								className='fv-help-block'
							>
								{error?.response?.data?.detail}
							</div>
						)}
					</div>
				)}
				{/* begin::Actions */}
				<div className='text-center pt-10 d-flex flex-stack'>
					<button
						type='reset'
						onClick={() => cancel()}
						className='btn btn-light me-3'
						data-kt-users-modal-action='cancel'
						disabled={formik.isSubmitting}
					>
						Отмена
					</button>

					<button
						type='submit'
						className='btn btn-primary'
						data-kt-users-modal-action='submit'
						disabled={
							formik.isSubmitting ||
							!formik.isValid ||
							!formik.touched ||
							isSetNewAmountLoading
						}
					>
						{formik.isSubmitting || isSetNewAmountLoading ? (
							<span className='indicator-label'>
								Пожалуйста подождите...{' '}
								<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
							</span>
						) : (
							<span className='indicator-label'>Подтвердить</span>
						)}
					</button>
				</div>
				{/* end::Actions */}
			</form>
		</>
	);
};

export { AutomaticModalBody };
